<template>
    <section>
        <div class="mb-4">
            <h2 style="font-size: x-large;"
                class="text-xl font-bold text-admin-primary border-l-4 border-admin-primary pl-4">
                AGENDA DE HOJE
            </h2>
        </div>

        <div v-if="atividades.length === 0"
            class="flex flex-col items-center justify-center py-8 px-4 text-center bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-10 h-10 text-gray-400 mb-3" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p class="text-gray-500 text-sm">Nenhum evento programado para hoje.</p>
        </div>

        <div v-else class="divide-y divide-gray-100">
            <div v-for="atividade in atividades" :key="atividade.id"
                class="flex gap-4 py-3 first:pt-0">

                <!-- Horário / Período -->
                <div class="flex-shrink-0 flex flex-col items-center justify-center rounded-lg px-3 py-2 text-center"
                    style="background-color: #2b529c; color: #ffffff; min-width: 72px;">
                    <template v-if="temHorario(atividade)">
                        <span class="text-sm font-bold" style="line-height: 1.2;">{{ atividade.time_inicio }}</span>
                        <span v-if="atividade.time_fim && atividade.time_fim !== atividade.time_inicio"
                            class="text-xs mt-0.5" style="opacity: 0.85;">
                            {{ atividade.time_fim }}
                        </span>
                    </template>
                    <template v-else-if="multiDias(atividade)">
                        <span class="font-semibold whitespace-pre-line text-center"
                            style="font-size: 11px; line-height: 1.3;">{{ periodoCompacto(atividade.periodo_completo_br) }}</span>
                    </template>
                    <template v-else>
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 mb-0.5" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span class="font-medium" style="font-size: 10px; line-height: 1.2;">Dia inteiro</span>
                    </template>
                </div>

                <!-- Conteúdo -->
                <div class="flex-1 min-w-0">
                    <div class="flex flex-wrap items-center gap-2 mb-1">
                        <h3 class="font-semibold text-sm" style="color: #2b529c; line-height: 1.3;">
                            {{ atividade.titulo }}
                        </h3>
                        <span v-if="atividade.tipo"
                            class="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
                            style="background-color: rgba(43,82,156,0.12); color: #2b529c;">
                            {{ atividade.tipo }}
                        </span>
                    </div>

                    <p v-if="atividade.periodo_completo_br && atividade.periodo_completo_br !== '—'"
                        class="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3 flex-shrink-0" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {{ atividade.periodo_completo_br }}
                    </p>

                    <p v-if="atividade.descricao"
                        class="text-xs text-gray-600 leading-relaxed mb-1"
                        style="display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">
                        {{ atividade.descricao }}
                    </p>

                    <div v-if="enderecoFormatado(atividade.endereco)"
                        class="flex items-center gap-1 text-xs text-gray-500 mt-1">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3 flex-shrink-0" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span style="display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden;">
                            {{ enderecoFormatado(atividade.endereco) }}
                        </span>
                    </div>

                    <!-- Cronograma do dia -->
                    <div v-if="atividade.cronograma_hoje && atividade.cronograma_hoje.length > 0"
                        class="mt-3 pt-3 border-t border-gray-100">
                        <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide bg-green-600 text-white mb-2">
                            Programação de hoje
                        </span>
                        <div v-for="(item, i) in atividade.cronograma_hoje" :key="i"
                            class="mb-3 last:mb-0">
                            <div class="flex items-start gap-2">
                                <span v-if="item.time"
                                    class="flex-shrink-0 text-xs font-bold px-1.5 py-0.5 rounded"
                                    style="background-color: rgba(43,82,156,0.1); color: #2b529c;">
                                    {{ item.time }}
                                </span>
                                <div class="flex-1 min-w-0">
                                    <p class="text-xs font-semibold text-gray-800 leading-snug">
                                        {{ item.descricao }}
                                    </p>
                                    <div v-if="enderecoFormatado(item.endereco)"
                                        class="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3 flex-shrink-0" fill="none"
                                            viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span>{{ enderecoFormatado(item.endereco) }}</span>
                                    </div>
                                    <p v-if="item.informacoes"
                                        class="text-xs text-gray-600 mt-1 leading-relaxed"
                                        style="white-space: pre-line;">
                                        {{ item.informacoes }}
                                    </p>
                                    <a v-if="item.link" :href="item.link" target="_blank" rel="noopener noreferrer"
                                        class="inline-flex items-center gap-1 text-xs font-medium mt-1 hover:underline"
                                        style="color: #2b529c;">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" fill="none"
                                            viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                        Ver detalhes
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="mt-4 text-right">
            <a href="/calendario-eventos"
                class="text-sm font-medium hover:underline inline-flex items-center gap-1"
                style="color: #2b529c;">
                Ver calendário completo
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                </svg>
            </a>
        </div>
    </section>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
    atividades: {
        type: Array,
        default: () => []
    }
})

const dataHoje = computed(() => {
    return new Intl.DateTimeFormat('pt-BR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    }).format(new Date()).replace(/^\w/, c => c.toUpperCase())
})

function temHorario(atividade) {
    const t = atividade.time_inicio
    return t && t !== '—' && t !== '-' && t.trim() !== ''
}

function multiDias(atividade) {
    const p = atividade.periodo_completo_br
    return p && p !== '—' && p.includes(' a ')
}

function periodoCompacto(periodo) {
    if (!periodo) return ''
    const match = periodo.match(/(\d{2}\/\d{2})(?:\/\d{4})?\s+a\s+(\d{2}\/\d{2})/)
    if (match) return `${match[1]}\na\n${match[2]}`
    return periodo.substring(0, 10)
}

function enderecoFormatado(endereco) {
    if (!endereco) return null
    if (typeof endereco === 'string') return endereco
    const partes = [endereco.logradouro, endereco.numero, endereco.bairro, endereco.cidade]
    return partes.filter(Boolean).join(', ') || null
}
</script>
